import { useEffect, useState } from 'react';
import { MessageCircle, Mail } from 'lucide-react';
import { useApi } from '@/lib/useApi';
import { Button, Card } from './ui';
import type { PublicConfig } from '@shared/types';

declare global {
  interface Window {
    Tawk_API?: {
      maximize?: () => void;
      hideWidget?: () => void;
      showWidget?: () => void;
      onLoad?: () => void;
    };
    Tawk_LoadStart?: Date;
  }
}

/*
 * Loaded once per tab, and only from the Help tab.
 *
 * Two reasons it is not in the app shell. A third-party script on every screen
 * is a third party watching pages with a landlord's tenants, bank references
 * and arrears on them, which is a bigger thing to hand over than a support
 * chat is worth. And a floating bubble over every screen is the single most
 * complained-about pattern on the web. It loads where somebody has gone
 * looking for help, and nowhere else.
 */
let loaded = false;

function loadTawk(propertyId: string, widgetId: string, onReady: () => void) {
  if (loaded) { onReady(); return; }
  loaded = true;

  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();
  window.Tawk_API.onLoad = onReady;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
  script.charset = 'UTF-8';
  script.setAttribute('crossorigin', '*');
  document.body.appendChild(script);
}

/**
 * Talking to the people who make PROVIA.
 *
 * Chat when it is switched on, email either way. Email is not the fallback for
 * a failed script — it is the answer for anybody who would rather write than
 * be watched typing, and for every hour of the day when nobody is at the other
 * end of a chat window.
 */
export function ContactPROVIA() {
  const { data: config } = useApi<PublicConfig>('/public/config');
  const [ready, setReady] = useState(false);

  const chat = config?.live_chat;
  const support = config?.contact.support ?? '';
  const product = config?.company.product ?? 'PROVIA';
  const company = config?.company.name ?? 'OPSERION';

  useEffect(() => {
    if (!chat?.enabled || !chat.property_id || !chat.widget_id) return undefined;

    loadTawk(chat.property_id, chat.widget_id, () => setReady(true));
    // Already loaded on a previous visit to this tab — onLoad will not fire again.
    if (window.Tawk_API?.showWidget) {
      window.Tawk_API.showWidget();
      setReady(true);
    }

    return () => {
      // The bubble follows the page otherwise, which is exactly what the note
      // above says not to do.
      try { window.Tawk_API?.hideWidget?.(); } catch { /* not loaded yet */ }
    };
  }, [chat?.enabled, chat?.property_id, chat?.widget_id]);

  return (
    <Card className="p-5 space-y-4">
      <div>
        <h3 className="font-semibold">Talk to us</h3>
        <p className="text-sm text-muted mt-1.5 leading-relaxed">
          {product} is built and run by {company}. A real person reads these — there is no
          ticket queue and no bot writing the replies.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        {chat?.enabled && (
          <Button
            variant="primary"
            icon={<MessageCircle className="h-4 w-4" />}
            disabled={!ready}
            onClick={() => window.Tawk_API?.maximize?.()}
          >
            {ready ? 'Start a live chat' : 'Loading chat…'}
          </Button>
        )}
        {support && (
          <a href={`mailto:${support}`}>
            <Button icon={<Mail className="h-4 w-4" />}>Email us</Button>
          </a>
        )}
      </div>

      {chat?.enabled && (
        <p className="text-xs text-faint leading-relaxed">
          Live chat is provided by tawk.to and loads only on this page, so no chat script runs
          while you are looking at your tenants’ records. If nobody is available it takes a
          message and we reply by email.
        </p>
      )}
    </Card>
  );
}
